import { compose, branch } from 'recompose';
import { withLogger, getExtendedLogger } from '@mindtickle/ui-shell-core/Logger';
//@ts-ignore
import Loader from '@mindtickle/loader';

import logger from '~/config/app.logger';
import ENV from '~/config/env';

import { withTranslationsInternal } from '~/components/Intl';
import { withRelay } from '~/components/Relay';
import { withSnowPlow } from '~/components/Snowplow';
import { withRedux } from '~/components/Redux';
import { withMixpanel } from '~/components/Mixpanel';
import { withPerformanceData } from '~/components/PerformanceMetrics';
import { withIntegrationHooksWrapper } from '~/components/IntegrationHooks';
import withDatadogApplicationScope from '~/components/Datadog/hocs/withDatadogApplicationScope';

import type { TMicroApps } from '~/components/Auth';

import { LOGGER_NAME } from '../config/constants';
import { EnvironmentConfig } from 'relay-runtime/lib/store/RelayModernEnvironment';

const validator = (condition: boolean, hocsType: string) => () => {
  if (condition) return true;
  const log = getExtendedLogger(LOGGER_NAME, logger);

  log(`skipping ${hocsType}`);
  return false;
};

const withRouteLevelIntegration = <P extends {}>({
  appName,
  streamName,
  enableRedux,
  graphqlEndpoint,
  collectorUrl,
  enableDatadog,
  enableSubscription,
  environmentOptions,
}: {
  appName: TMicroApps;
  streamName: string;
  enableRedux?: boolean;
  graphqlEndpoint?: string;
  collectorUrl?: string;
  enableDatadog: boolean;
  enableSubscription?: boolean;
  environmentOptions?: EnvironmentConfig;
}) =>
  compose<P, P>(
    branch(validator(!!enableRedux, 'redux'), withRedux<P>()),
    branch(validator(!!enableRedux && !!ENV.TRACKING, 'mixpanel'), withMixpanel<P>()),
    withTranslationsInternal<P>({ appName, loader: <Loader type="Full" /> }),
    branch(
      validator(!!graphqlEndpoint, 'relay'),
      withRelay<P>({
        enableSubscription: !!enableSubscription,
        graphqlEndpoint: graphqlEndpoint!,
        environmentOptions,
      })
    ),
    branch(
      validator(enableDatadog, 'datadog'),
      withDatadogApplicationScope<P>({ appName, streamName })
    ),
    branch(
      validator(!!collectorUrl, 'snowplow'),
      withSnowPlow<P>({
        collectorUrl: collectorUrl!,
        namespace: `${streamName}-${appName}`,
      })
    ),
    withLogger<P>({ namespace: `${streamName}-${appName}` }),
    withPerformanceData<P>({ appName }),
    withIntegrationHooksWrapper(graphqlEndpoint)
  );

export default withRouteLevelIntegration;
